import {
  CreateInstitutionShareholdingPayload,
  ShareholdingType,
  useBusinessEntitiesOfCompany,
  useCreateShareholding,
  useStockClassList,
} from '@commenda/api';
import { AddEntityBottomsheet } from '../../common/BusinessEntity/AddEntityBottomsheet';
import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { OwnershipRelationshipType } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useController } from 'react-hook-form';
import {
  CollapsibleModuleCard,
  FormActions,
} from '../../../form-builder/components/new-incorp-layout';
import { useZodForm } from '../../../hooks/useZodForm';
import { ENTITY_RELATIONSHIP_OPTIONS } from '../constants';
import { buildAddEntityShareholderSchema } from '../schemas';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';
import { OwnershipPercentField } from './OwnershipPercentField';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';
import { applyOwnershipApiError } from './applyOwnershipApiError';
import { HscCheckbox } from './HscCheckbox';
import {
  InheritableUltimateBeneficialOwnersSection,
  type InheritedUboSelection,
} from './InheritableUltimateBeneficialOwnersSection';
import { StakeholderStockClassField } from './StakeholderStockClassField';
import { useStockClassStaging } from './useStockClassStaging';
import { useGetDilutedPercent } from './useGetDilutedPercent';
import { useHscThresholdAutoToggle } from './useHscThresholdAutoToggle';
import { useOwnershipFormDerivations } from './useOwnershipFormDerivations';

interface AddEntityShareholderFormProps {
  corporationId: number;
  companyId: number;
  onCancel: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const AddCorporateShareholderForm = ({
  corporationId,
  companyId,
  onCancel,
  onSuccess,
  onError,
}: AddEntityShareholderFormProps) => {
  const shareholdingMutation = useCreateShareholding(corporationId);
  const nouns = useEntityOwnershipNouns(corporationId);
  // The schema needs this before the form is built.
  const { data: formStockClasses } = useStockClassList(corporationId);
  const capitalOnlyLlc = isCapitalOnlyLlc(nouns.tracksUnits, formStockClasses);
  const [cardOpen, setCardOpen] = useState(true);
  const [addEntityOpen, setAddEntityOpen] = useState(false);
  const [optimisticEntity, setOptimisticEntity] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // null = "use defaults" (inherit all parent UBOs at default control type).
  const [inheritedSelections, setInheritedSelections] = useState<
    InheritedUboSelection[] | null
  >(null);

  const { data: workspaceEntities } = useBusinessEntitiesOfCompany(companyId, {
    includeNonControlled: true,
  });
  const linkOptions = useMemo(() => {
    const entities = workspaceEntities?.entities ?? [];
    const baseOptions = entities
      .filter((e) => e.id !== corporationId)
      .map((e) => ({
        label: e.generalCorporationInfo.legalName,
        value: String(e.id),
      }));
    if (
      optimisticEntity &&
      !baseOptions.some((o) => o.value === String(optimisticEntity.id))
    ) {
      return [
        { label: optimisticEntity.name, value: String(optimisticEntity.id) },
        ...baseOptions,
      ];
    }
    return baseOptions;
  }, [workspaceEntities?.entities, corporationId, optimisticEntity]);

  const { percentScale, percentStep, getBudget, uboThreshold } =
    useOwnershipFormDerivations(corporationId);
  const schema = useMemo(
    () => buildAddEntityShareholderSchema(percentScale, getBudget, nouns.classFieldNouns.singular, !capitalOnlyLlc),
    [percentScale, getBudget, nouns.classFieldNouns.singular, capitalOnlyLlc],
  );

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useZodForm({
    schema,
    // No ownershipPercent default — required, placeholder until typed.
    defaultValues: {},
  });

  const { field: hscField } = useController({ control, name: 'hasSignificantControl' });
  const hsc = (hscField.value as boolean | undefined) ?? false;

  const linkedId = watch('stakeholderCorporationId');
  const {
    stagedStockClass,
    setStagedStockClass,
    existingStockClassId,
    selectedStockClassName,
    stockClassBody,
    ownedSharesFromPercent,
  } = useStockClassStaging(corporationId, watch('stockClassId'));

  const getDilutedPercent = useGetDilutedPercent(corporationId, {
    stagedAuthorizedShares: stagedStockClass?.authorizedShares ?? null,
  });
  useHscThresholdAutoToggle({
    control,
    uboThresholdPercent: uboThreshold,
    getDilutedPercent,
    stockClassId: existingStockClassId ?? null,
  });

  // Inheritance rules:
  //   parent link changes               ⇒ drop selections (stale director ids)
  //   HSC=false                         ⇒ clear selections (no inheritance)
  //   HSC=true (any cause), null state  ⇒ section default-seeds all UBOs
  //                                       with INDIRECT_OWNERSHIP on data
  //                                       arrival; covers manual check
  //                                       below threshold and threshold
  //                                       cross-up
  // User customisations survive while HSC stays on (selections becomes
  // non-null, section won't re-seed). Re-toggling HSC off→on wipes them.
  useEffect(() => {
    setInheritedSelections(null);
  }, [linkedId]);

  useEffect(() => {
    if (!hsc) setInheritedSelections(null);
  }, [hsc]);

  const submit = handleSubmit(
    async (values) => {
      // Block rather than write a 0-share holding when the class has no
      // authorized count to issue the percentage against.
      const ownedShares = ownedSharesFromPercent(values.ownershipPercent);
      if (ownedShares === null) {
        setError('ownershipPercent', {
          type: 'server',
          message:
            `This ${nouns.classFieldNouns.lowerSingular} has no total set — set it before assigning a percentage.`,
        });
        onError?.(SUBMIT_VALIDATION_TOAST);
        return;
      }

      const proceed = async () => {
        try {
          const institution: CreateInstitutionShareholdingPayload = {
            stakeholderCorporationId: values.stakeholderCorporationId,
            ownedShares,
            relationshipType: values.relationshipType,
            hasSignificantControl: values.hasSignificantControl,
            ...stockClassBody,
            inheritedUbos: values.hasSignificantControl
              ? (inheritedSelections ?? [])
              : [],
          };
          await shareholdingMutation.mutateAsync({ type: ShareholdingType.INSTITUTION, institution });
          onSuccess?.(`Entity ${nouns.lowerSingular} added.`);
          onCancel();
        } catch (err) {
          applyOwnershipApiError(err, setError, onError);
        }
      };
      await proceed();
    },
    () => onError?.(SUBMIT_VALIDATION_TOAST),
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-2xl pb-8xl">
      <CollapsibleModuleCard
        title={`Entity ${nouns.lowerSingular} details`}
        isExpanded={cardOpen}
        onExpandedChange={setCardOpen}
      >
        <FieldWrapper>
          <Label required>Link to existing entity in your account</Label>
          <Controller
            control={control}
            name="stakeholderCorporationId"
            render={({ field }) => (
              <SingleCombobox
                canClearSelection={false}
                options={linkOptions}
                value={field.value !== undefined && field.value !== null ? String(field.value) : ''}
                setValue={(v) => field.onChange(v ? Number(v) : undefined)}
                placeholder="Select an entity"
                addItemConfig={{
                  shouldAllowAdd: true,
                  onAddItem: () => setAddEntityOpen(true),
                  placeholder: 'Add new entity',
                }}
              />
            )}
          />
          {errors.stakeholderCorporationId?.message && (
            <InputErrorMessage
              message={errors.stakeholderCorporationId.message}
            />
          )}
        </FieldWrapper>

        <FieldWrapper>
          <Label required>Relationship</Label>
          <Controller
            control={control}
            name="relationshipType"
            render={({ field }) => (
              <SingleCombobox
                canClearSelection={false}
                options={ENTITY_RELATIONSHIP_OPTIONS}
                value={field.value ?? ''}
                setValue={(v) =>
                  field.onChange(v as OwnershipRelationshipType)
                }
                placeholder="Select relationship"
              />
            )}
          />
          {errors.relationshipType?.message && (
            <InputErrorMessage message={errors.relationshipType.message} />
          )}
        </FieldWrapper>

        <StakeholderStockClassField
          control={control}
          name="stockClassId"
          corporationId={corporationId}
          stagedDraft={stagedStockClass}
          setStagedDraft={setStagedStockClass}
          onError={onError}
        />

        <OwnershipPercentField
          control={control}
          name="ownershipPercent"
          step={percentStep}
          errorMessage={errors.ownershipPercent?.message}
          stockClassName={selectedStockClassName}
        />

        <HscCheckbox
          checked={hsc}
          onCheckedChange={(v) =>
            hscField.onChange(typeof v === 'boolean' ? v : false)
          }
          uboThresholdPercent={uboThreshold}
        />

        {hsc && !!linkedId && (
          <InheritableUltimateBeneficialOwnersSection
            corporationId={corporationId}
            parentCorporationId={linkedId}
            parentEntityName={
              linkOptions.find((o) => o.value === String(linkedId))?.label
            }
            selections={inheritedSelections}
            onSelectionsChange={setInheritedSelections}
          />
        )}
      </CollapsibleModuleCard>

      <FormActions
        hideBack
        showCancel
        onCancel={onCancel}
        nextLabel={`Add ${nouns.lowerSingular}`}
        isLoading={shareholdingMutation.isPending}
      />

      <AddEntityBottomsheet
        isOpen={addEntityOpen}
        onClose={() => setAddEntityOpen(false)}
        companyId={companyId}
        onSuccess={async (corp) => {
          setOptimisticEntity({ id: corp.id, name: corp.name });
          setValue('stakeholderCorporationId', corp.id);
          setAddEntityOpen(false);
        }}
      />
    </form>
  );
};
